import { Instagram, Phone, Music, Guitar, Download, Smartphone, Heart, Mail, ArrowRight, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const Footer = () => {
  const { data: settings } = useSiteSettings();
  const instagramUrl = settings?.instagram_url || "https://www.instagram.com/_maycksoncosta";

  return (
    <footer className="relative bg-[hsl(var(--navy-dark))] text-white/60 overflow-hidden">
      {/* Decorative top border */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      
      {/* Glow effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto max-w-6xl px-4 relative z-10">
        {/* Main footer content */}
        <div className="py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4 group">
              <Guitar className="h-8 w-8 text-primary group-hover:text-[hsl(var(--gold-light))] transition-colors" />
              <div className="flex flex-col leading-none">
                <span className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-serif)' }}>LOUVE</span>
                <span className="text-[9px] text-white/30 tracking-[0.2em] uppercase">Hinos Avulsos</span>
              </div>
            </Link>
            <p className="text-sm leading-relaxed mb-6 max-w-xs">
              A maior coleção de cifras de hinos avulsos para violão. Projeto dedicado a levar louvor, fé e esperança através da música cristã.
            </p>
            <div className="flex items-center gap-3">
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-primary hover:border-primary/30 hover:bg-primary/10 transition-all duration-300" 
                aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://wa.me/65999778086" target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-green-400 hover:border-green-400/30 hover:bg-green-400/10 transition-all duration-300" 
                aria-label="WhatsApp">
                <Phone className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
              <span className="w-5 h-px bg-primary" />
              Navegação
            </h4>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/", label: "Início" },
                { to: "/hinos-avulsos", label: "Hinos Avulsos" },
                { to: "/baixar-cifras", label: "Baixar Cifras" },
                { to: "/ouvir-hinos", label: "Ouvir Hinos" },
                { to: "/blog", label: "Blog Gospel" },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="group flex items-center gap-2 hover:text-primary transition-colors duration-200">
                    <ArrowRight className="h-3 w-3 text-primary/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tools */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
              <span className="w-5 h-px bg-primary" />
              Ferramentas
            </h4>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/afinador", label: "Afinador de Violão", icon: <Guitar className="h-3.5 w-3.5" /> },
                { to: "/instalar", label: "Instalar App", icon: <Smartphone className="h-3.5 w-3.5" /> },
                { to: "/louve-store", label: "Louve Store", icon: <Heart className="h-3.5 w-3.5" /> },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="flex items-center gap-2 hover:text-primary transition-colors duration-200">
                    <span className="text-primary/50">{link.icon}</span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Legal */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
              <span className="w-5 h-px bg-primary" />
              Contato
            </h4>
            <div className="space-y-3 text-sm mb-6">
              <a href="https://wa.me/65999778086" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Phone className="h-4 w-4 text-primary/50" /> WhatsApp
              </a>
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Instagram className="h-4 w-4 text-primary/50" /> Instagram
              </a>
            </div>
            <div className="pt-4 border-t border-white/5 space-y-2 text-xs">
              <Link to="/politica-de-privacidade" className="block hover:text-primary transition-colors">Política de Privacidade</Link>
              <Link to="/termos-de-uso" className="block hover:text-primary transition-colors">Termos de Uso</Link>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">
            © {new Date().getFullYear()} Louve Hinos Avulsos – Todos os direitos reservados
          </p>
          <p className="text-xs text-white/20 flex items-center gap-1">
            Feito com <Heart className="h-3 w-3 text-primary/50" /> para a glória de Deus
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
